import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { WorkspaceShell } from "./WorkspaceShell";
import {
  AttendeesTab, StationsTab, ProductionStageSelect,
} from "@/pages/Admin";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import { Check, X, ScanLine, Link as LinkIcon, Download } from "lucide-react";

const PAYMENT_STATUS_LABELS = {
  awaiting_invoice:  "Awaiting Invoice",
  invoice_sent:      "Invoice Sent",
  payment_received:  "Payment Received",
  complete:          "Registration Complete",
  cancelled:         "Cancelled",
  paid:              "Payment Received",
  partially_paid:    "Partially Paid",
  refunded:          "Refunded",
  waived:            "Waived",
  unpaid:            "Awaiting Invoice",
  pending:           "Awaiting Invoice",
};
const formatPaymentStatus = (s) => (s ? (PAYMENT_STATUS_LABELS[s] || s.replace(/_/g, " ")) : "—");

/**
 * Registration Workspace — Prime Suppliers · Attendees · Stations · Magic Links · Check-In.
 */
export default function RegistrationWorkspace() {
  const [exhibitors, setExhibitors] = useState([]);
  const [magicLinks, setMagicLinks] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/admin/exhibitors").then(({ data }) => setExhibitors(data.exhibitors || [])).catch(() => {});
    api.get("/admin/magic-links").then(({ data }) => setMagicLinks(data.magic_links || [])).catch(() => {});
    api.get("/industry-categories").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
  }, []);

  const setStatus = async (id, status) => {
    const { data } = await api.put(`/admin/exhibitors/${id}/status`, { status });
    setExhibitors((es) => es.map((x) => x.id === id ? data.exhibitor : x));
    toast.success(`Marked ${status}.`);
  };
  const togglePayment = async (e) => {
    const next = e.payment_status === "paid" ? "unpaid" : "paid";
    const { data } = await api.put(`/admin/exhibitors/${e.id}/payment`, { payment_status: next });
    setExhibitors((es) => es.map((x) => x.id === e.id ? data.exhibitor : x));
  };
  const setCategory = async (id, industry_category) => {
    try {
      const { data } = await api.put(`/admin/exhibitors/${id}`, { industry_category });
      setExhibitors((es) => es.map((x) => x.id === id ? data.exhibitor : x));
      toast.success("Industry category updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update category");
    }
  };

  const downloadPaymentReport = async () => {
    try {
      const { data } = await api.get("/admin/exhibitors/payment-report.csv", { responseType: "blob" });
      const blob = new Blob([data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `prime-supplier-payment-report-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Payment report downloaded.");
    } catch {
      toast.error("Could not download payment report.");
    }
  };

  const tabs = [
    {
      value: "suppliers", label: "Prime Suppliers",
      render: () => (
        <div className="card-flat overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left">
              <tr className="border-b border-zinc-200">
                <Th>Company</Th><Th>Contact</Th><Th>Website</Th><Th>Industry</Th><Th>Payment</Th><Th>Status</Th><Th>Stage</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {exhibitors.map((e) => (
                <tr key={e.id} className="border-b border-zinc-100 last:border-0" data-testid={`reg-supplier-${e.id}`}>
                  <Td>
                    <Link to={`/admin/supplier/${e.id}`} className="font-medium hover:text-[#A39171] hover:underline underline-offset-4"
                          data-testid={`reg-open-project-${e.id}`}>
                      {e.company_name}
                    </Link>
                    <div className="text-xs text-zinc-500">{e.email}</div>
                  </Td>
                  <Td>
                    <div>{e.primary_contact}<span className="text-[10px] uppercase tracking-[0.14em] text-[#A39171] ml-2">· Primary</span></div>
                    <div className="text-xs text-zinc-500">{e.contact_title || ""}</div>
                    <div className="text-xs text-zinc-500">{e.phone}</div>
                    {e.additional_representative && (
                      <div className="mt-1.5 pt-1.5 border-t border-zinc-100" data-testid={`reg-rep-additional-${e.id}`}>
                        <div>{e.additional_representative.first_name} {e.additional_representative.last_name}
                          <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 ml-2">· Additional</span>
                        </div>
                        <div className="text-xs text-zinc-500">{e.additional_representative.title || ""}</div>
                        <div className="text-xs text-zinc-500 truncate max-w-[220px]">{e.additional_representative.email}</div>
                        {e.additional_representative.phone && (
                          <div className="text-xs text-zinc-500">{e.additional_representative.phone}</div>
                        )}
                      </div>
                    )}
                  </Td>
                  <Td>
                    {e.website ? (
                      <a href={/^https?:\/\//.test(e.website) ? e.website : `https://${e.website}`}
                         target="_blank" rel="noreferrer"
                         className="text-xs text-[#A39171] hover:underline underline-offset-4 truncate inline-block max-w-[160px]"
                         data-testid={`reg-website-${e.id}`}
                         title={e.website}>
                        {e.website.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0]}
                      </a>
                    ) : <span className="text-xs text-zinc-400">—</span>}
                  </Td>
                  <Td>
                    <select value={e.industry_category || ""}
                            onChange={(ev) => setCategory(e.id, ev.target.value)}
                            data-testid={`reg-industry-${e.id}`}
                            className="text-xs border border-zinc-200 hover:border-black px-2 py-1 bg-white outline-none">
                      <option value="">— Not set —</option>
                      {categories.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                    </select>
                  </Td>
                  <Td><span className={`pill ${["payment_received","complete","paid"].includes(e.payment_status) ? "bg-[#A39171]/15 border-[#A39171]" : ""}`}>{formatPaymentStatus(e.payment_status)}</span></Td>
                  <Td><span className={`pill ${e.status === "approved" ? "bg-black text-white border-black" : ""}`}>{e.status}</span></Td>
                  <Td><ProductionStageSelect exhibitor={e} onSaved={(u) => setExhibitors((es) => es.map((x) => x.id === u.id ? u : x))} /></Td>
                  <Td>
                    <div className="flex gap-2 flex-wrap">
                      <Link to={`/admin/supplier/${e.id}`} className="px-3 py-1 text-xs uppercase tracking-[0.18em] bg-black text-white" data-testid={`reg-open-invoicing-${e.id}`}>Open Project</Link>
                      {e.status !== "approved" && <button onClick={() => setStatus(e.id, "approved")} data-testid={ADMIN.approveBtn} className="px-3 py-1 text-xs uppercase tracking-[0.18em] border border-zinc-300"><Check size={12} /></button>}
                      {e.status !== "rejected" && <button onClick={() => setStatus(e.id, "rejected")} data-testid={ADMIN.rejectBtn} className="px-3 py-1 text-xs uppercase tracking-[0.18em] border border-zinc-300"><X size={12} /></button>}
                    </div>
                  </Td>
                </tr>
              ))}
              {exhibitors.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-zinc-500">No suppliers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      ),
    },
    { value: "attendees", label: "Attendees", render: () => <AttendeesTab /> },
    { value: "stations", label: "Stations", render: () => <StationsTab exhibitors={exhibitors} /> },
    {
      value: "magic", label: "Magic Links",
      render: () => (
        <div className="card-flat p-6" data-testid={ADMIN.magicLinksTab}>
          <p className="text-sm text-zinc-500 mb-4">Live magic-link inventory. Suppliers use these to sign in.</p>
          <div className="grid gap-3">
            {magicLinks.map((m) => (
              <div key={m.token} className="border-b border-zinc-100 last:border-0 py-3 flex flex-wrap items-center gap-3 justify-between">
                <div>
                  <div className="text-sm font-medium">{m.email}</div>
                  <div className="text-xs text-zinc-500">Issued {new Date(m.created_at).toLocaleString()} {m.used ? "· used" : ""}</div>
                </div>
                <a href={`/verify?token=${m.token}`} className="btn-outline-black h-9 px-4 text-xs"><LinkIcon size={12} /> Open</a>
              </div>
            ))}
            {magicLinks.length === 0 && <p className="text-sm text-zinc-500">No pending magic links.</p>}
          </div>
        </div>
      ),
    },
  ];

  return (
    <WorkspaceShell
      title="Registration"
      subtitle="Attendees, payments, stations, and check-in — the front door of the Summit."
      tabs={tabs}
      dataTestId="ws-title-registration"
      headerActions={
        <div className="flex gap-2 flex-wrap">
          <button onClick={downloadPaymentReport}
                  data-testid="reg-download-payment-report"
                  className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white inline-flex items-center gap-2">
            <Download size={12} /> Payment Report CSV
          </button>
          <Link to="/checkin" className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white" data-testid="reg-link-checkin">
            <ScanLine size={12} /> Event-Day Check-In
          </Link>
        </div>
      }
    />
  );
}

const Th = ({ children }) => <th className="text-xs uppercase tracking-[0.18em] text-zinc-500 px-4 py-3">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-4 align-top">{children}</td>;
